import React from "react";

/**
 * Moved from oem's `~/components/general/useBoxTokenRefresh` so the portal's
 * Box explorers refresh on the same signals. oem re-exports it from there.
 */

// Service code dispatches this on the window when a Box API call returns 401.
// Anyone holding a Box token refetch should listen and refetch.
export const BOX_TOKEN_EXPIRED_EVENT = "box-token-expired";

type RefetchFn = () => unknown;

// Box tokens are opaque, so we can't schedule a precise expiry-aware refresh
// like we do for chat. Instead, refetch on every signal that the user may have
// just re-engaged or the current token may have died:
//   - tab becomes visible    → laptop wake / alt-tab return
//   - browser goes online    → network reconnect
//   - Box API returns 401    → service-level signal that token is dead
// On a warm BE cache these refetches are essentially free (one DB read).
export const useBoxTokenRefresh = (refetch: RefetchFn) => {
  const refetchRef = React.useRef(refetch);
  refetchRef.current = refetch;

  React.useEffect(() => {
    const trigger = () => refetchRef.current();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") trigger();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", trigger);
    window.addEventListener(BOX_TOKEN_EXPIRED_EVENT, trigger);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", trigger);
      window.removeEventListener(BOX_TOKEN_EXPIRED_EVENT, trigger);
    };
  }, []);
};

export default useBoxTokenRefresh;
